/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { DialogClose } from "@radix-ui/react-dialog";
import {
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  useEffect,
  useState,
} from "react";

import { useDynamicViewport } from "../../../../hooks/useDynamicViewport";
import { END_OF_BROWSER_ASYNC_QUEUE } from "../../../../lib/constants/endOfBrowserAsyncQueue.constants";
import { VIEWPORT_TABLET } from "../../../../lib/constants/viewports/viewportTablet";
import type { DropdownMenuItemProps } from "../DropdownMenuItem/DropdownMenuItem.emotion";
import { DropdownMenuItem } from "../DropdownMenuItem/DropdownMenuItem.emotion";
import type { ModalItemProps } from "../ModalItem/ModalItem.emotion";
import { ModalItem } from "../ModalItem/ModalItem.emotion";

export type ModalDropdownMenuItemProps = {
  children?: ReactNode;
  dropdownMenuItemCSS?: Interpolation;
  dropdownMenuItemProps?: DropdownMenuItemProps;
  modalItemCSS?: Interpolation;
  modalItemProps?: ModalItemProps;
  selected?: boolean;
  /**
   * No event param is available here because we need to make this event handler
   * asynchronous to avoid a race condition
   */
  onSelect?: () => void;
  minDesktopWidth?: number;
  options?: {
    closeModalOnSelect?: boolean;
  };
};

export const ModalDropdownMenuItem = forwardRef<
  HTMLButtonElement | HTMLDivElement,
  ModalDropdownMenuItemProps
>(
  (
    {
      children,
      dropdownMenuItemCSS = {},
      dropdownMenuItemProps = {} as NonNullable<
        ModalDropdownMenuItemProps["dropdownMenuItemProps"]
      >,
      modalItemCSS = {},
      modalItemProps = {} as NonNullable<
        ModalDropdownMenuItemProps["modalItemProps"]
      >,
      selected,
      onSelect,
      minDesktopWidth = VIEWPORT_TABLET,
      options = {} as NonNullable<ModalDropdownMenuItemProps["options"]>,
      ...props
    },
    ref,
  ) => {
    const { closeModalOnSelect = true } = options;

    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => setIsMounted(true), []);

    const isDesktopViewport = useDynamicViewport({
      minimumWidth: minDesktopWidth,
      defaultState: undefined,
    });

    const handleSelect: typeof onSelect = (...selectProps) => {
      // avoid race conditions where selecting an item closes a menu and leaves us stranded with pointer-events: none;
      setTimeout(() => onSelect?.(...selectProps), END_OF_BROWSER_ASYNC_QUEUE);
    };

    if (!isMounted) return null;

    if (isDesktopViewport)
      return (
        <DropdownMenuItem
          ref={ref as ForwardedRef<HTMLDivElement>}
          selected={selected}
          onSelect={handleSelect}
          {...props}
          css={dropdownMenuItemCSS}
          {...dropdownMenuItemProps}
        >
          {dropdownMenuItemProps.children || children}
        </DropdownMenuItem>
      );

    const ModalItemComponent = (
      <ModalItem
        ref={ref as ForwardedRef<HTMLButtonElement>}
        selected={selected}
        onSelect={handleSelect}
        {...props}
        css={modalItemCSS}
        {...modalItemProps}
      >
        {modalItemProps.children || children}
      </ModalItem>
    );

    if (closeModalOnSelect) {
      return <DialogClose asChild>{ModalItemComponent}</DialogClose>;
    } else {
      return ModalItemComponent;
    }
  },
);

ModalDropdownMenuItem.displayName = "ModalDropdownMenuItem";
